import { Button, Col, DatePicker, Form, Select, TimePicker, Tooltip } from "antd"
import { Rule } from "antd/lib/form"
import { FormListFieldData } from "antd/lib/form/FormList"
import moment from "moment-timezone"
import numeral from "numeral"
import React, { useMemo } from "react"
import { Trash } from "react-feather"
import { useTranslation } from "react-i18next"

import { DestinationFieldsRule } from "Components/forms/rules/perDiem.rules"
import { getPopupAnchor } from "Shared/utils"
import { doFilterSelect } from "Shared/utils/helpers"

import { generatePerDiemDestinationLabel } from "../helpers"
import { FormPerDiemDestinationItinerary } from "../perDiem.form"
import { usePerDiemDestinations } from "Shared/hooks/perDiemDestination.hooks"
import { ExclamationCircleFilled } from "@ant-design/icons"
import { HoverableInfo } from "../../auxilliary/HoverableInfo"
import { PerDiemDestination } from "@finway-group/shared/lib/models"

const PER_DIEM_UNSUPPORETED_LIMIT = "2021-01-01"

interface PerDiemDestinationFieldInterface {
    elementField: FormListFieldData
    index: number
    remove: (i: number) => void
    tripDestinations: FormPerDiemDestinationItinerary[]
    adjustDestinationsOnChange: () => void
}
export const PerDiemDestinationField: React.FC<PerDiemDestinationFieldInterface> = ({ elementField, index, remove, tripDestinations, adjustDestinationsOnChange }) => {
    const { t } = useTranslation()
    const perDiemDestinationTree = usePerDiemDestinations(true)

    const spans = {
        destination: index === 0 ? 24 : 8,
        startDate: index === 0 ? 6 : 0,
        startTime: index === 0 ? 6 : 0,
        endDate: index === 0 ? 6 : 8,
        endTime: 6,
        trash: index === 0 ? 0 : 2,
    }

    const rules = DestinationFieldsRule(tripDestinations, index)

    const renderPerDiemSelectionOption = (dropdownOption: PerDiemDestination) => {
        if (!dropdownOption.children || dropdownOption.children.length === 0) {
            return (
                <Select.Option key={`per-diem-country-selection-${dropdownOption._id}`} label={generatePerDiemDestinationLabel(dropdownOption)} value={dropdownOption._id}>
                    {generatePerDiemDestinationLabel(dropdownOption)}
                </Select.Option>
            )
        }
        return (
            <Select.OptGroup key={`per-diem-country-selection-${dropdownOption._id}`} label={dropdownOption.destination}>
                {(dropdownOption.children as PerDiemDestination[]).map(renderPerDiemSelectionOption)}
            </Select.OptGroup>
        )
    }

    const perDiemSelectOptions = useMemo(() => perDiemDestinationTree.map(renderPerDiemSelectionOption), []) // No need to re-generate the list of select options/tree

    /**
     * Determines the disabled dates for the start date picker.
     * Blocks the user from picking dates after the current destination's end date.
     */
    const startDisabledDate = (current: moment.Moment) => {
        if (current.isSameOrBefore(PER_DIEM_UNSUPPORETED_LIMIT)) return true
        const currentDestination = tripDestinations[index]

        if (!currentDestination?.endDate) return false

        return current.isAfter(currentDestination.endDate, "minute")
    }

    /**
     * Determines the disabled dates for the return/transfer date picker.
     * Blocks the user from picking dates before the previous destination or after the next destinaton
     */
    const endDisabledDate = (current: moment.Moment) => {
        if (current.isSameOrBefore(PER_DIEM_UNSUPPORETED_LIMIT)) return true
        const currentDestination = tripDestinations[index]

        // Current destination's startDate always be the previous's endDate, managed by adjustStartDateDestinations
        if (!currentDestination?.startDate) return false

        // Block dates that is after the next destination's end date
        const nextDestination = tripDestinations[index + 1]
        if (nextDestination?.endDate && current.isAfter(nextDestination.endDate, "minute")) return true

        // Block dates that is before the current destination's start date
        return current.isBefore(currentDestination.startDate, "minute")
    }

    /**
     * Determines the hours of the timepicker that should be blocked.
     * Should evaluate when the endDate of the current destination is equal with the endDate of the other destination
     */
    const disabledHours = () => {
        // Dont do check if the date hasnt been defined yet.
        if (!tripDestinations[index]) return []

        // Get the previous destination end date (transfer date) OR current destination start date as a fallback
        const previousMovementDate = tripDestinations[index - 1]?.endDate ?? tripDestinations[index]?.startDate
        if (!previousMovementDate) return []

        // When the current destination date is not the same as the previous date, do nothing
        if (tripDestinations[index].endDate?.isAfter(previousMovementDate, "day")) return []

        // But if it's the same, make sure that the user cannot select the hours before the previous date
        let disabledUntil = previousMovementDate.hour()

        return disabledUntil ? [...Array(disabledUntil).keys()] : []
    }

    /**
     * Determines the minutes of the timepicker that should be blocked.
     * Should evaluate when the endDate of the current destinaton is equal with the endDate of other destination, and have the same hour set.
     */
    const disabledMinutes = (selectedHour: number) => {
        // Dont do check if the date hasnt been defined yet.
        if (!tripDestinations[index]) return []

        // Get the previous destination end date (transfer date) OR current destination start date as a fallback
        const previousMovementDate = tripDestinations[index - 1]?.endDate ?? tripDestinations[index]?.startDate

        if (!previousMovementDate) return []

        // When the current destination date is not the same as the previous date, do not disabled anything in the minutes
        if (tripDestinations[index].endDate?.isAfter(previousMovementDate, "day")) return []

        // When the selected hour is more then the previous date's hour, do not disable anything in the minutes
        if (selectedHour > previousMovementDate.hours()) return []

        const disabledUntil = previousMovementDate.minute()
        return disabledUntil ? [...Array(disabledUntil).keys(), disabledUntil] : []
    }

    // date and time fields is referring to the same property so the datepicker and timepicker have the same "name" (startDate and endDate) prop.
    return (
        <React.Fragment key={`per-diem-destination-field-${index}`}>
            <Col span={spans.destination}>
                <Form.Item
                    {...elementField}
                    label={t(`input:request.per_diem.${tripDestinations.length > 1 ? "nth_" : ""}destination`, { nth: numeral(index + 1).format("0o") })}
                    name={[elementField.name, "destination"]}
                    key="destination"
                    rules={rules.destination}
                >
                    <Select filterOption={doFilterSelect} showSearch className="per-diem-select" getPopupContainer={getPopupAnchor()} onChange={adjustDestinationsOnChange}>
                        {perDiemSelectOptions}
                    </Select>
                </Form.Item>
            </Col>
            <Col span={spans.startDate}>
                <Form.Item {...elementField} label={t("input:request.per_diem.start_date")} name={[elementField.name, "startDate"]} key="startDate" rules={rules.startDate}>
                    <DatePicker onChange={adjustDestinationsOnChange} disabledDate={startDisabledDate} format={"DD/MM/YYYY"} getPopupContainer={getPopupAnchor()} />
                </Form.Item>
            </Col>
            <Col span={spans.startTime}>
                <Form.Item {...elementField} label={t("input:request.per_diem.start_time")} name={[elementField.name, "startDate"]} key="startTime" rules={rules.startTime}>
                    <TimePicker onChange={adjustDestinationsOnChange} format={"HH:mm"} getPopupContainer={getPopupAnchor()} popupClassName="timepicker-display-fix" />
                </Form.Item>
            </Col>
            <Col span={spans.endDate}>
                <Form.Item
                    {...elementField}
                    label={
                        index === tripDestinations.length - 1 ? (
                            t("input:request.per_diem.return_date")
                        ) : (
                            <>
                                {t("input:request.per_diem.transfer_date")}
                                <HoverableInfo message={t("info:per_diem.transfer_date")} />
                            </>
                        )
                    }
                    name={[elementField.name, "endDate"]}
                    key="endDate"
                    rules={rules.endDate}
                >
                    <DatePicker
                        disabledDate={endDisabledDate}
                        className="w-full"
                        onChange={adjustDestinationsOnChange}
                        format={"DD/MM/YYYY"}
                        getPopupContainer={getPopupAnchor()}
                    />
                </Form.Item>
            </Col>
            <Col span={spans.endTime}>
                <Form.Item
                    {...elementField}
                    label={
                        index === tripDestinations.length - 1 ? (
                            t("input:request.per_diem.return_time")
                        ) : (
                            <>
                                {t("input:request.per_diem.transfer_time")}
                                <HoverableInfo message={t("info:per_diem.transfer_time")} />
                            </>
                        )
                    }
                    name={[elementField.name, "endDate"]}
                    key="endTime"
                    rules={rules.endTime}
                >
                    <TimePicker
                        className="w-full"
                        onChange={adjustDestinationsOnChange}
                        format={"HH:mm"}
                        getPopupContainer={getPopupAnchor()}
                        disabledHours={disabledHours}
                        disabledMinutes={disabledMinutes}
                        popupClassName="timepicker-display-fix"
                    />
                </Form.Item>
            </Col>
            <Col span={spans.trash}>
                <Button
                    className="mt-28"
                    onClick={() => {
                        remove(index)
                        adjustDestinationsOnChange()
                    }}
                >
                    <Trash />
                </Button>
            </Col>
        </React.Fragment>
    )
}
